/**
 * One code path for every write that may happen inside a customer site with
 * no signal.
 *
 *   saved   → the row is in Postgres.
 *   queued  → the request never reached PostgREST (offline / transport
 *             failure); the payload is in the offline outbox and replays on
 *             reconnect.
 *   failed  → PostgREST answered with an error (RLS, validation, FK). Those
 *             are permanent, so they are shown to the technician and are NOT
 *             queued — retrying a 42501 five times helps nobody.
 */
import { enqueue, type OutboxKind } from './offlineOutbox';
import { errorMessage, friendlyDbError, isOnline, isTransportError, type ErrorLike } from './errors';

export type PersistOutcome =
  | { status: 'saved' }
  | { status: 'queued'; reason: 'offline' | 'transport'; detail: string | null }
  | { status: 'failed'; message: string };

async function queue(kind: OutboxKind, payload: Record<string, unknown>, reason: 'offline' | 'transport', detail: string | null): Promise<PersistOutcome> {
  try {
    await enqueue(kind, payload);
    return { status: 'queued', reason, detail };
  } catch (e) {
    return {
      status: 'failed',
      message: `Offline storage is unavailable (${errorMessage(e)}). The change was not saved.`,
    };
  }
}

export async function persistOrQueue(
  kind: OutboxKind,
  payload: Record<string, unknown>,
  run: () => PromiseLike<{ error: ErrorLike | null }>,
): Promise<PersistOutcome> {
  if (!isOnline()) return queue(kind, payload, 'offline', null);
  try {
    const { error } = await run();
    if (!error) return { status: 'saved' };
    if (isTransportError(error)) return queue(kind, payload, 'transport', errorMessage(error));
    return { status: 'failed', message: friendlyDbError(error) };
  } catch (e) {
    if (isTransportError(e)) return queue(kind, payload, 'transport', errorMessage(e));
    return { status: 'failed', message: friendlyDbError(e) };
  }
}

/** Toast copy for a queued outcome, shared by every form. */
export function queuedMessage(what: string, outcome: Extract<PersistOutcome, { status: 'queued' }>): string {
  return outcome.reason === 'offline'
    ? `You are offline. ${what} is saved on this device and will sync when the connection returns.`
    : `Connection problem. ${what} is saved on this device and will sync automatically.`;
}
